import clsx from 'clsx';
import { FocusError } from 'focus-formik-error';
import {
  Field,
  Form as FormikForm,
  FormikHelpers,
  FormikProvider,
  useFormik,
} from 'formik';
import MobileDetect from 'mobile-detect';
// import Image from 'next/image';
import { useEffect } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import * as Yup from 'yup';

import styles from './Form-additional.module.scss';

import divider from './assets/divider.png';

const API_URL = 'https://siteapi.tvgla.com/email-tvgla-info';
const API_METHOD = 'POST';
const RE_CAPTCHA_ACTION = 'one_wish_form_submit';

const EMAIL_REGEX =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const FormSchema = Yup.object({}).shape({
  name: Yup.string()
    .default('')
    .max(50, 'Must be under 50 characters.')
    .required('Name is Required'),
  company: Yup.string().default('').max(50, 'Must be under 50 characters.'),
  email: Yup.string()
    .default('')
    .matches(EMAIL_REGEX, 'Invalid Email Address')
    .required('Email Address is Required'),
  phone: Yup.string().default('').max(30, 'Must be under 30 characters.'),
  reason: Yup.string()
    .default(' ')
    .max(50, 'Must be under 50 characters.')
    .min(2, 'Please select a Reason for Reaching Out.')
    .required('Please select a Reason for Reaching Out.'),
  message: Yup.string()
    .default('')
    .max(500, 'Must be under 500 characters.')
    .required('Message is Required'),
  title: Yup.string().default(''),
  department: Yup.string().default(''),
  employeeCount: Yup.string().default(''),
  industry: Yup.string().default(''),
});

type Values = Yup.InferType<typeof FormSchema>;

type Props = {
  refreshPriority: number;
  debug?: boolean;
};

const isBrowser = typeof window !== 'undefined';

const Form = ({}: Props) => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  /**
   * Handles submission of the form
   * @param values Form values
   * @param param1 Formik Helper variables
   */
  const sendFormData = async <V extends Values>(
    values: V,
    { setStatus, setSubmitting }: FormikHelpers<V>
  ) => {
    setStatus({ type: 'loading' });
    setSubmitting(true);

    // Sending error if google recaptcha was not initialized
    if (!executeRecaptcha) {
      setStatus({
        type: 'error',
        message: 'Internal Error. Please try submitting the form again.',
      });
      setSubmitting(false);

      return;
    }

    const headers = new Headers();
    headers.append('Content-Type', 'application/json');

    const type = new MobileDetect(window.navigator.userAgent);

    const userAgent = navigator.userAgent.toLowerCase();
    const isTablet = /(ipad|tablet|playbook|silk)|(android(?!.*mobile))/g.test(
      userAgent
    );
    const isMac = /(macintosh|applewebkit)/g.test(userAgent);

    const device = isTablet ? 'Tablet' : type.mobile() ? 'Mobile' : 'Desktop';
    const system = type.os() ? type.os() : isMac ? 'Mac' : type.userAgent();

    const {
      name,
      company,
      email,
      phone,
      reason,
      message,
      title,
      department,
      employeeCount,
      industry,
    } = values;

    try {
      const token = await executeRecaptcha(RE_CAPTCHA_ACTION);

      const body = JSON.stringify({
        ReceiverAddress: 'info@tvgla.com',
        Subject: `Let's Connect: ${reason.trim()}`,
        HTMLBody: `<p>Name: ${name.trim()}</p>
        <p>Company: ${company.trim()}</p>
        <p>Email: ${email.trim()}</p>
        <p>Phone: ${phone.trim()}</p>
        <p>Reason for reaching out: ${reason.trim()}</p>
        <br />
        <p>Message: ${message.trim()}.</p>
        <p>[Sent from {${device}}, {${system}}]</p>`,
        TXTBody: `Name: ${name.trim()}.
        Company: ${company.trim()}.
        Email: ${email.trim()}.
        Phone: ${phone.trim()}.
        Reason for reaching out: ${reason.trim()}.
        Message: ${message.trim()}.
        [Sent from {${device}}, {${system}}]`,

        Title: title,
        Department: department,
        EmployeeCount: employeeCount,
        Industry: industry,

        Token: token,
      });

      const res = await fetch(API_URL, { method: API_METHOD, headers, body });
      const data = await res.json();

      if (!res.ok || data.errors.length > 0) {
        if (
          data.errors.length !== 1 ||
          data.errors[0].type !== 'invalid-fields-error'
        ) {
          throw new Error(
            'Sorry, unable to process your submission now. Please try again later.'
          );
        }
      }

      if (isBrowser && window.dataLayer) {
        window.dataLayer.push({
          event: 'form submission',
          wishCompany: company.trim(),
          wishReason: reason.trim(),
          wishMessage: message.trim(),
        });
      }

      setStatus({ type: 'complete', message: res.statusText });
    } catch (error) {
      if (error instanceof Error) {
        setStatus({ type: 'error', message: error.message });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: FormSchema.getDefault(),
    validationSchema: FormSchema,
    onSubmit: sendFormData,
  });

  const { status, errors, touched, values, resetForm, isSubmitting } = formik;
  const isCompleteForm = status?.type === 'complete';

  useEffect(() => {
    if (isCompleteForm) setTimeout(() => resetForm(), 5000);
  }, [isCompleteForm, resetForm]);

  return (
    <section className={styles.section} id="contact-us">
      <img alt="" className={styles.divider} src={divider} />

      <FormikProvider value={formik}>
        <FocusError formik={formik} />

        <div className={styles['title-container']}>
          <h2 className={styles.title}>
            Let&apos;s
            <br />
            connect
          </h2>
        </div>

        <FormikForm className={styles.form}>
          <h3 className={styles.subtitle}>
            Submit your information and we&apos;ll make sure someone from the{' '}
            <mark>team matching your request contacts you.</mark>
          </h3>

          {/* Honeypots - START */}
          <div className={styles.other}>
            <Field
              autoComplete="off"
              className={styles.input}
              disabled={isCompleteForm}
              id="input-title"
              name="title"
              placeholder="Title"
            />
            {touched.title && errors.title && (
              <p className={styles.error}>
                <svg
                  className={styles['error-icon']}
                  fill="currentColor"
                  height="1em"
                  stroke="currentColor"
                  strokeWidth="0"
                  viewBox="0 0 512 512"
                  width="1em"
                  xmlns="http://www.w3.org/2000/svg">
                  <path d="M504 256c0 136.997-111.043 248-248 248S8 392.997 8 256C8 119.083 119.043 8 256 8s248 111.083 248 248zm-248 50c-25.405 0-46 20.595-46 46s20.595 46 46 46 46-20.595 46-46-20.595-46-46-46zm-43.673-165.346l7.418 136c.347 6.364 5.609 11.346 11.982 11.346h48.546c6.373 0 11.635-4.982 11.982-11.346l7.418-136c.375-6.874-5.098-12.654-11.982-12.654h-63.383c-6.884 0-12.356 5.78-11.981 12.654z"></path>
                </svg>
                <span className={styles['error-text']}>{errors.title}</span>
              </p>
            )}
            <Field
              autoComplete="off"
              className={styles.input}
              disabled={isCompleteForm}
              id="input-department"
              name="department"
              placeholder="Department"
            />
            {touched.department && errors.department && (
              <p className={styles.error}>
                <svg
                  className={styles['error-icon']}
                  fill="currentColor"
                  height="1em"
                  stroke="currentColor"
                  strokeWidth="0"
                  viewBox="0 0 512 512"
                  width="1em"
                  xmlns="http://www.w3.org/2000/svg">
                  <path d="M504 256c0 136.997-111.043 248-248 248S8 392.997 8 256C8 119.083 119.043 8 256 8s248 111.083 248 248zm-248 50c-25.405 0-46 20.595-46 46s20.595 46 46 46 46-20.595 46-46-20.595-46-46-46zm-43.673-165.346l7.418 136c.347 6.364 5.609 11.346 11.982 11.346h48.546c6.373 0 11.635-4.982 11.982-11.346l7.418-136c.375-6.874-5.098-12.654-11.982-12.654h-63.383c-6.884 0-12.356 5.78-11.981 12.654z"></path>
                </svg>
                <span className={styles['error-text']}>
                  {errors.department}
                </span>
              </p>
            )}
            <Field
              autoComplete="off"
              className={styles.input}
              disabled={isCompleteForm}
              id="input-employeeCount"
              name="employeeCount"
              placeholder="Employee count"
            />
            {touched.employeeCount && errors.employeeCount && (
              <p className={styles.error}>
                <svg
                  className={styles['error-icon']}
                  fill="currentColor"
                  height="1em"
                  stroke="currentColor"
                  strokeWidth="0"
                  viewBox="0 0 512 512"
                  width="1em"
                  xmlns="http://www.w3.org/2000/svg">
                  <path d="M504 256c0 136.997-111.043 248-248 248S8 392.997 8 256C8 119.083 119.043 8 256 8s248 111.083 248 248zm-248 50c-25.405 0-46 20.595-46 46s20.595 46 46 46 46-20.595 46-46-20.595-46-46-46zm-43.673-165.346l7.418 136c.347 6.364 5.609 11.346 11.982 11.346h48.546c6.373 0 11.635-4.982 11.982-11.346l7.418-136c.375-6.874-5.098-12.654-11.982-12.654h-63.383c-6.884 0-12.356 5.78-11.981 12.654z"></path>
                </svg>
                <span className={styles['error-text']}>
                  {errors.employeeCount}
                </span>
              </p>
            )}
            <Field
              autoComplete="off"
              className={styles.input}
              disabled={isCompleteForm}
              id="input-industry"
              name="industry"
              placeholder="Industry"
            />
            {touched.industry && errors.industry && (
              <p className={styles.error}>
                <svg
                  className={styles['error-icon']}
                  fill="currentColor"
                  height="1em"
                  stroke="currentColor"
                  strokeWidth="0"
                  viewBox="0 0 512 512"
                  width="1em"
                  xmlns="http://www.w3.org/2000/svg">
                  <path d="M504 256c0 136.997-111.043 248-248 248S8 392.997 8 256C8 119.083 119.043 8 256 8s248 111.083 248 248zm-248 50c-25.405 0-46 20.595-46 46s20.595 46 46 46 46-20.595 46-46-20.595-46-46-46zm-43.673-165.346l7.418 136c.347 6.364 5.609 11.346 11.982 11.346h48.546c6.373 0 11.635-4.982 11.982-11.346l7.418-136c.375-6.874-5.098-12.654-11.982-12.654h-63.383c-6.884 0-12.356 5.78-11.981 12.654z"></path>
                </svg>
                <span className={styles['error-text']}>{errors.industry}</span>
              </p>
            )}
          </div>
          {/* Honeypots - END */}

          <div>
            <Field
              className={styles.input}
              disabled={isCompleteForm}
              id="input-name"
              name="name"
              placeholder="Full name"
            />
            {touched.name && errors.name && (
              <p className={styles.error}>
                <svg
                  className={styles['error-icon']}
                  fill="currentColor"
                  height="1em"
                  stroke="currentColor"
                  strokeWidth="0"
                  viewBox="0 0 512 512"
                  width="1em"
                  xmlns="http://www.w3.org/2000/svg">
                  <path d="M504 256c0 136.997-111.043 248-248 248S8 392.997 8 256C8 119.083 119.043 8 256 8s248 111.083 248 248zm-248 50c-25.405 0-46 20.595-46 46s20.595 46 46 46 46-20.595 46-46-20.595-46-46-46zm-43.673-165.346l7.418 136c.347 6.364 5.609 11.346 11.982 11.346h48.546c6.373 0 11.635-4.982 11.982-11.346l7.418-136c.375-6.874-5.098-12.654-11.982-12.654h-63.383c-6.884 0-12.356 5.78-11.981 12.654z"></path>
                </svg>
                <span className={styles['error-text']}>{errors.name}</span>
              </p>
            )}
          </div>

          <div>
            <Field
              className={styles.input}
              disabled={isCompleteForm}
              id="input-company"
              name="company"
              placeholder="Company"
            />
            {touched.company && errors.company && (
              <p className={styles.error}>
                <svg
                  className={styles['error-icon']}
                  fill="currentColor"
                  height="1em"
                  stroke="currentColor"
                  strokeWidth="0"
                  viewBox="0 0 512 512"
                  width="1em"
                  xmlns="http://www.w3.org/2000/svg">
                  <path d="M504 256c0 136.997-111.043 248-248 248S8 392.997 8 256C8 119.083 119.043 8 256 8s248 111.083 248 248zm-248 50c-25.405 0-46 20.595-46 46s20.595 46 46 46 46-20.595 46-46-20.595-46-46-46zm-43.673-165.346l7.418 136c.347 6.364 5.609 11.346 11.982 11.346h48.546c6.373 0 11.635-4.982 11.982-11.346l7.418-136c.375-6.874-5.098-12.654-11.982-12.654h-63.383c-6.884 0-12.356 5.78-11.981 12.654z"></path>
                </svg>
                <span className={styles['error-text']}>{errors.company}</span>
              </p>
            )}
          </div>

          <div>
            <Field
              className={styles.input}
              disabled={isCompleteForm}
              id="input-email"
              name="email"
              placeholder="Email"
              type="email"
            />
            {touched.email && errors.email && (
              <p className={styles.error}>
                <svg
                  className={styles['error-icon']}
                  fill="currentColor"
                  height="1em"
                  stroke="currentColor"
                  strokeWidth="0"
                  viewBox="0 0 512 512"
                  width="1em"
                  xmlns="http://www.w3.org/2000/svg">
                  <path d="M504 256c0 136.997-111.043 248-248 248S8 392.997 8 256C8 119.083 119.043 8 256 8s248 111.083 248 248zm-248 50c-25.405 0-46 20.595-46 46s20.595 46 46 46 46-20.595 46-46-20.595-46-46-46zm-43.673-165.346l7.418 136c.347 6.364 5.609 11.346 11.982 11.346h48.546c6.373 0 11.635-4.982 11.982-11.346l7.418-136c.375-6.874-5.098-12.654-11.982-12.654h-63.383c-6.884 0-12.356 5.78-11.981 12.654z"></path>
                </svg>
                <span className={styles['error-text']}>{errors.email}</span>
              </p>
            )}
          </div>

          <div>
            <Field
              className={clsx(styles.input)}
              disabled={isCompleteForm}
              id="input-phone"
              name="phone"
              placeholder="Phone"
              type="phone"
            />
            {touched.phone && errors.phone && (
              <p className={styles.error}>
                <svg
                  className={styles['error-icon']}
                  fill="currentColor"
                  height="1em"
                  stroke="currentColor"
                  strokeWidth="0"
                  viewBox="0 0 512 512"
                  width="1em"
                  xmlns="http://www.w3.org/2000/svg">
                  <path d="M504 256c0 136.997-111.043 248-248 248S8 392.997 8 256C8 119.083 119.043 8 256 8s248 111.083 248 248zm-248 50c-25.405 0-46 20.595-46 46s20.595 46 46 46 46-20.595 46-46-20.595-46-46-46zm-43.673-165.346l7.418 136c.347 6.364 5.609 11.346 11.982 11.346h48.546c6.373 0 11.635-4.982 11.982-11.346l7.418-136c.375-6.874-5.098-12.654-11.982-12.654h-63.383c-6.884 0-12.356 5.78-11.981 12.654z"></path>
                </svg>
                <span className={styles['error-text']}>{errors.phone}</span>
              </p>
            )}
          </div>

          <div>
            <Field
              as="select"
              disabled={isCompleteForm}
              id="select-reason"
              name="reason"
              placeholder="Reason for reaching out"
              type="reason"
              className={clsx(
                styles.select,
                values.reason.trim().length && styles.selected,
                styles.asterisks
              )}>
              <option disabled value=" ">
                Reason for reaching out
              </option>
              <option value="I'd like to learn more about TVGla">
                I&apos;d like to learn more about TVGla
              </option>
              <option value="I'm searching for a Performance Creative Agency">
                I&apos;m searching for a Performance Creative Agency
              </option>
              <option value="I'd like to apply for a job">
                I&apos;d like to apply for a job
              </option>
              <option value="I have a product or service I'd like to share">
                I have a product or service I&apos;d like to share
              </option>
              <option value="Something else">Something else</option>
            </Field>
            {touched.reason && errors.reason && (
              <p className={styles.error}>
                <svg
                  className={styles['error-icon']}
                  fill="currentColor"
                  height="1em"
                  stroke="currentColor"
                  strokeWidth="0"
                  viewBox="0 0 512 512"
                  width="1em"
                  xmlns="http://www.w3.org/2000/svg">
                  <path d="M504 256c0 136.997-111.043 248-248 248S8 392.997 8 256C8 119.083 119.043 8 256 8s248 111.083 248 248zm-248 50c-25.405 0-46 20.595-46 46s20.595 46 46 46 46-20.595 46-46-20.595-46-46-46zm-43.673-165.346l7.418 136c.347 6.364 5.609 11.346 11.982 11.346h48.546c6.373 0 11.635-4.982 11.982-11.346l7.418-136c.375-6.874-5.098-12.654-11.982-12.654h-63.383c-6.884 0-12.356 5.78-11.981 12.654z"></path>
                </svg>
                <span className={styles['error-text']}>{errors.reason}</span>
              </p>
            )}
          </div>

          <div>
            <label className={clsx(styles.label)} htmlFor="input-message">
              Your message
            </label>
            <Field
              className={styles['input-box']}
              component="textarea"
              disabled={isCompleteForm}
              id="input-message"
              name="message"
            />
            {touched.message && errors.message && (
              <p className={styles.error}>
                <svg
                  className={styles['error-icon']}
                  fill="currentColor"
                  height="1em"
                  stroke="currentColor"
                  strokeWidth="0"
                  viewBox="0 0 512 512"
                  width="1em"
                  xmlns="http://www.w3.org/2000/svg">
                  <path d="M504 256c0 136.997-111.043 248-248 248S8 392.997 8 256C8 119.083 119.043 8 256 8s248 111.083 248 248zm-248 50c-25.405 0-46 20.595-46 46s20.595 46 46 46 46-20.595 46-46-20.595-46-46-46zm-43.673-165.346l7.418 136c.347 6.364 5.609 11.346 11.982 11.346h48.546c6.373 0 11.635-4.982 11.982-11.346l7.418-136c.375-6.874-5.098-12.654-11.982-12.654h-63.383c-6.884 0-12.356 5.78-11.981 12.654z"></path>
                </svg>
                <span className={styles['error-text']}>{errors.message}</span>
              </p>
            )}
          </div>

          <aside className={styles.aside}>
            This site is protected by reCAPTCHA and the Google{' '}
            <a href="https://policies.google.com/privacy" target="_blank">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="https://policies.google.com/terms" target="_blank">
              Terms of Service
            </a>{' '}
            apply.
          </aside>

          <div>
            {status?.type === 'error' ? (
              <p className={styles.error}>
                <svg
                  className={styles['error-icon']}
                  fill="currentColor"
                  height="1em"
                  stroke="currentColor"
                  strokeWidth="0"
                  viewBox="0 0 512 512"
                  width="1em"
                  xmlns="http://www.w3.org/2000/svg">
                  <path d="M504 256c0 136.997-111.043 248-248 248S8 392.997 8 256C8 119.083 119.043 8 256 8s248 111.083 248 248zm-248 50c-25.405 0-46 20.595-46 46s20.595 46 46 46 46-20.595 46-46-20.595-46-46-46zm-43.673-165.346l7.418 136c.347 6.364 5.609 11.346 11.982 11.346h48.546c6.373 0 11.635-4.982 11.982-11.346l7.418-136c.375-6.874-5.098-12.654-11.982-12.654h-63.383c-6.884 0-12.356 5.78-11.981 12.654z"></path>
                </svg>
                <span className={styles['error-text']}>{status.message}</span>
              </p>
            ) : (
              <></>
            )}
            <button
              disabled={isCompleteForm || isSubmitting}
              type="submit"
              className={clsx(
                styles.submit,
                isSubmitting && styles.sending,
                isCompleteForm && styles.complete
              )}>
              {isCompleteForm
                ? 'Thank you! Someone will contact you soon!'
                : 'Send'}
              <span className={clsx(styles.loading)}>
                {[...Array(3)].map((_, index) => (
                  <span
                    key={`loading-element-${index}`}
                    style={{ animationDelay: `${index * 0.2}s` }}
                  />
                ))}
              </span>
            </button>
          </div>
        </FormikForm>
      </FormikProvider>
    </section>
  );
};

export default Form;
